import { useSelector } from '@tanstack/react-form';
import { titleize } from 'inflected';
import { useMemo } from 'react';
import { z } from 'zod';
import { BpmDateString, toBpmDateString } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { Event, EventType, Importance } from '@blisspointmedia/bpm-types/dist/OracleActivity';
import {
  BandConfig,
  EditOracleEventBody,
  OracleEventEditField,
  OracleEventEditFields,
  OracleEventEditResult,
  OracleEventEdits
} from '@blisspointmedia/bpm-types/dist/OracleApi';
import { blankTo, requiredChoice, requiredText } from '@Components/Forms/form-field-utils';
import { useAppForm } from '@Components/Forms/useAppForm';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { useSetError } from '@src/redux/modals';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import { useEditEvent } from '../useOracleEventMutations';

export const EDIT_FIELD_LABELS = {
  ...Object.fromEntries(OracleEventEditFields.map(field => [field, titleize(field)])),
  details: 'Description',
  recommendedAction: 'Next Steps',
  importance: 'Priority'
} as Record<OracleEventEditField, string>;

const blankToNull = blankTo(null);

export const createEditEventSchema = (requireDetails: boolean) =>
  z.object({
    title: requiredText('Title is required'),
    eventType: requiredChoice<EventType>('Choose an event type'),
    channel: z.custom<string | null>(),
    details: requireDetails ? requiredText('Description is required') : z.string().trim(),
    // The API clears next steps with null, but details has no null form, so blank details stay ''.
    recommendedAction: blankToNull,
    date: requiredChoice<BpmDateString>('Pick a date'),
    importance: requiredChoice<Importance>('Choose a priority')
  });

export type EventEditDraft = z.input<ReturnType<typeof createEditEventSchema>>;
type EditEventSubmission = z.output<ReturnType<typeof createEditEventSchema>>;

export const draftFromEvent = (event: Event): EventEditDraft => ({
  title: event.title,
  eventType: event.eventType,
  channel: event.channel ?? null,
  details: event.details,
  recommendedAction: event.recommendedAction ?? '',
  // TIME-precision dates are full ISO strings; the date picker edits the calendar day only.
  date: toBpmDateString(event.date.slice(0, 10)),
  importance: event.importance
});

// A band holds several weights, so keep the current weight when it already sits in the chosen
// band; otherwise take the band's highest.
export const weightForImportance = (
  importance: Importance,
  currentWeight: number | undefined,
  bands: BandConfig
): number | null => {
  if (importance === Importance.NONE) return null;
  const band = bands.weights[importance];
  if (currentWeight !== undefined && band.includes(currentWeight)) return currentWeight;
  return band.length > 0 ? Math.max(...band) : null;
};

// Only fields whose submitted value differs from the event become edits. Without `bands` (counts
// not loaded yet) an importance edit goes out without its weight.
export const buildEventEdits = (
  event: Event,
  submission: EditEventSubmission,
  bands: BandConfig | undefined
): OracleEventEdits => {
  const original = draftFromEvent(event);
  const edits: OracleEventEdits = {};
  if (submission.title !== original.title.trim()) {
    edits.title = submission.title;
  }
  if (submission.eventType !== original.eventType) {
    edits.eventType = submission.eventType;
  }
  if (submission.channel !== original.channel) {
    edits.channel = submission.channel;
  }
  if (submission.details !== original.details.trim()) {
    edits.details = submission.details;
  }
  if (submission.recommendedAction !== blankToNull.parse(original.recommendedAction)) {
    edits.recommendedAction = submission.recommendedAction;
  }
  if (submission.date !== original.date) {
    edits.date = submission.date;
  }
  if (submission.importance !== original.importance) {
    edits.importance = submission.importance;
    const weight = bands
      ? weightForImportance(submission.importance, event.weight, bands)
      : undefined;
    if (weight !== undefined && weight !== (event.weight ?? null)) {
      edits.weight = weight;
    }
  }
  return edits;
};

// Unchanged fields come from the live event, so a refetched value isn't sent back as an edit.
export const overlayChangedFields = (
  event: Event,
  value: EventEditDraft,
  changedFields: readonly (keyof EventEditDraft)[]
): EventEditDraft => ({
  ...draftFromEvent(event),
  ...Object.fromEntries(changedFields.map(name => [name, value[name]]))
});

// A folded card stands for every event in its group, so an edit has to reach all of them.
export const buildEditRequest = (event: Event, edits: OracleEventEdits): EditOracleEventBody => ({
  cid: event.cid,
  eventId: event.id,
  edits,
  ...(event.foldedIds && { foldedIds: event.foldedIds })
});

export const formatEditFailures = (results: OracleEventEditResult[]): string =>
  results
    .flatMap(result =>
      result.success ? [] : [`${EDIT_FIELD_LABELS[result.edit.field]}: ${result.error}`]
    )
    .join('\n');

interface UseEditEventFormArgs {
  event: Event;
  bands: BandConfig | undefined;
  requireDetails: boolean;
  onSaved: () => void;
}

export const useEditEventForm = ({
  event,
  bands,
  requireDetails,
  onSaved
}: UseEditEventFormArgs) => {
  const setError = useSetError();
  const { mutateAsync: submitEdits } = useEditEvent();
  const schema = useMemo(() => createEditEventSchema(requireDetails), [requireDetails]);

  // Until the first edit, new defaultValues replace the form values, so a refetch still shows
  // through; after it, every field keeps its value until reset.
  const form = useAppForm({
    defaultValues: draftFromEvent(event),
    validators: { onChange: schema },
    onSubmit: async ({ value, formApi }) => {
      try {
        const changedFields = schema
          .keyof()
          .options.filter(name => formApi.getFieldMeta(name)?.isDirty);
        const edits = buildEventEdits(
          event,
          schema.parse(overlayChangedFields(event, value, changedFields)),
          bands
        );
        // Save turns on for any raw change, so a whitespace-only edit reaches here with no edits.
        if (Object.keys(edits).length === 0) {
          formApi.reset();
          onSaved();
          return;
        }
        const { results } = await submitEdits(buildEditRequest(event, edits));
        const failures = formatEditFailures(results);
        if (failures) {
          setError({ title: "Some edits didn't save", message: failures });
          return;
        }
        formApi.reset();
        onSaved();
        Mixpanel.track(MxE.RecordSaved, { page: PAGE_NAMES.ACTIVITY_FEED, entity: 'event' });
      } catch (e) {
        setError({ message: `Couldn't save edits: ${e.message}`, reportError: e });
      }
    }
  });
  const isSaving = useSelector(form.store, state => state.isSubmitting);

  return { form, isSaving };
};

export interface EventFormEditor {
  form: ReturnType<typeof useEditEventForm>['form'];
  disabled: boolean;
}
